/**
 * ClickSpamDetectionPage — read-only spam-signal report.
 * Shows GET /stats summary tiles + filterable GET /?signal&severity&flagged&since events table.
 */
import { useMemo, useState } from "react";
import { useClickSpamDetectionList, useClickSpamDetectionStats } from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TH = "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";
const INPUT =
	"rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-1 focus:ring-green-600";
const LABEL = "text-xs font-medium uppercase tracking-wider text-zinc-500";

const SIGNALS = [
	"datacenter_ip",
	"vpn",
	"tor",
	"proxy",
	"residential_proxy",
	"rapid_fire",
	"bot_ua",
	"duplicate_click",
	"other",
];
const SEVERITIES = ["low", "medium", "high"];

type FlaggedFilter = "" | "1" | "0";

function fmtTime(ts: number): string {
	if (!ts) return "—";
	const d = new Date(ts * 1000);
	return d.toISOString().replace("T", " ").slice(0, 19);
}

function truncate(s: string, n = 40): string {
	if (!s) return "—";
	return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

export function ClickSpamDetectionPage() {
	const [signal, setSignal] = useState<string>("");
	const [severity, setSeverity] = useState<string>("");
	const [flagged, setFlagged] = useState<FlaggedFilter>("");
	const [sinceHours, setSinceHours] = useState<string>("");

	const filters = useMemo(() => {
		const f: {
			signal?: string;
			severity?: string;
			flagged?: "1" | "0";
			since?: number;
		} = {};
		if (signal) f.signal = signal;
		if (severity) f.severity = severity;
		if (flagged === "1" || flagged === "0") f.flagged = flagged;
		const hrs = Number(sinceHours);
		if (sinceHours && Number.isFinite(hrs) && hrs > 0) {
			f.since = Math.floor(Date.now() / 1000) - Math.floor(hrs * 3600);
		}
		return f;
	}, [signal, severity, flagged, sinceHours]);

	const statsQuery = useClickSpamDetectionStats();
	const listQuery = useClickSpamDetectionList(filters);
	const items = listQuery.data?.items ?? [];

	const flaggedRate =
		statsQuery.data && statsQuery.data.total > 0
			? Math.round((statsQuery.data.flagged / statsQuery.data.total) * 100)
			: 0;

	return (
		<main
			data-testid="click-spam-detection-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Fraud signal report
				</p>
				<h1 className="text-3xl font-semibold text-green-700">Click spam detection</h1>
				<p className="text-sm text-zinc-500">
					Inspect flagged click events, filter by signal, severity and recency.
				</p>
			</header>

			<section
				className="grid grid-cols-1 gap-4 sm:grid-cols-3"
				aria-label="Click spam detection summary"
				data-testid="click-spam-detection-summary"
			>
				<div className={CARD}>
					<p className={LABEL}>Total events</p>
					<p className="mt-2 text-2xl font-semibold tabular-nums text-zinc-900">
						{statsQuery.data ? statsQuery.data.total.toLocaleString() : "—"}
					</p>
				</div>
				<div className={CARD}>
					<p className={LABEL}>Flagged</p>
					<p className="mt-2 text-2xl font-semibold tabular-nums text-green-700">
						{statsQuery.data ? statsQuery.data.flagged.toLocaleString() : "—"}
					</p>
				</div>
				<div className={CARD}>
					<p className={LABEL}>Flagged rate</p>
					<p className="mt-2 text-2xl font-semibold tabular-nums text-zinc-900">
						{statsQuery.data ? `${flaggedRate}%` : "—"}
					</p>
				</div>
			</section>

			<section
				className={`${CARD} flex flex-col gap-4`}
				aria-label="Click spam detection filters"
				data-testid="click-spam-detection-filters"
			>
				<div className="flex flex-wrap items-end gap-3">
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Signal</span>
						<select
							aria-label="Signal filter"
							className={INPUT}
							value={signal}
							onChange={(e) => setSignal(e.target.value)}
							data-testid="click-spam-detection-signal"
						>
							<option value="">All signals</option>
							{SIGNALS.map((s) => (
								<option key={s} value={s}>
									{s}
								</option>
							))}
						</select>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Severity</span>
						<select
							aria-label="Severity filter"
							className={INPUT}
							value={severity}
							onChange={(e) => setSeverity(e.target.value)}
							data-testid="click-spam-detection-severity"
						>
							<option value="">All severities</option>
							{SEVERITIES.map((s) => (
								<option key={s} value={s}>
									{s}
								</option>
							))}
						</select>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Flagged</span>
						<select
							aria-label="Flagged filter"
							className={INPUT}
							value={flagged}
							onChange={(e) => setFlagged(e.target.value as FlaggedFilter)}
							data-testid="click-spam-detection-flagged"
						>
							<option value="">All</option>
							<option value="1">Flagged only</option>
							<option value="0">Not flagged</option>
						</select>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Since (hours)</span>
						<input
							type="number"
							min={0}
							step={1}
							aria-label="Since hours"
							placeholder="e.g. 24"
							className={`${INPUT} w-32`}
							value={sinceHours}
							onChange={(e) => setSinceHours(e.target.value)}
							data-testid="click-spam-detection-since"
						/>
					</label>
				</div>
			</section>

			<section
				className={CARD}
				aria-label="Click spam detection events"
				data-testid="click-spam-detection-events"
			>
				{listQuery.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="click-spam-detection-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p className="text-sm text-zinc-500" data-testid="click-spam-detection-empty">
						No events match the current filters.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse"
							data-testid="click-spam-detection-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className={TH}>When</th>
									<th className={TH}>Event</th>
									<th className={TH}>Click</th>
									<th className={TH}>Signal</th>
									<th className={TH}>Severity</th>
									<th className={TH}>Score</th>
									<th className={TH}>Flagged</th>
									<th className={TH}>IP</th>
									<th className={TH}>User agent</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200">
								{items.map((row) => (
									<tr
										key={row.id}
										data-testid={`click-spam-detection-row-${row.id}`}
									>
										<td className={`${TD} tabular-nums`}>
											{fmtTime(row.created_at)}
										</td>
										<td className={`${TD} font-mono text-xs`}>
											{truncate(row.event_id, 16)}
										</td>
										<td className={`${TD} font-mono text-xs`}>
											{truncate(row.click_id, 16)}
										</td>
										<td className={TD}>{row.signal}</td>
										<td className={TD}>{row.severity}</td>
										<td className={`${TD} tabular-nums`}>
											{row.score.toLocaleString()}
										</td>
										<td className={TD}>
											{row.flagged ? (
												<span className="inline-flex items-center rounded-[6px] bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
													Yes
												</span>
											) : (
												<span className="inline-flex items-center rounded-[6px] bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-500">
													No
												</span>
											)}
										</td>
										<td className={`${TD} font-mono text-xs`}>
											{truncate(row.ip, 20)}
										</td>
										<td className={`${TD} font-mono text-xs`}>
											{truncate(row.user_agent, 40)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default ClickSpamDetectionPage;
